import * as React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { PanelMarker } from '../../models/PanelMarker';
import { MathUtils } from '../../utils';

const blood_drop_current = require('../../assets/imgs/blood_drop_black.png');
const blood_drop_previous = require('../../assets/imgs/blood_drop_blue_50.png');

type Props = {
    panelMarker: PanelMarker;
    chartContainerClass: string;
};

type State = {
    panelMarker: PanelMarker;
    currentChartData: number;
    previousChartData: number;
}
// Use this flag to see actual percentFromMean values on chart
const DEBUG: boolean = false;
const DELAY: number = 2500;

export class RDWReferenceRangeChart extends React.Component<Props, State> {
    state: State = {
        panelMarker: this.props.panelMarker,
        currentChartData: undefined,
        previousChartData: undefined
    };

    componentWillReceiveProps(nextProps) {
        const { panelMarker } = nextProps;
        this.setState({
            panelMarker,
            currentChartData: this.createChartData(panelMarker),
            previousChartData: this.createChartData(panelMarker.previousPanelMarker)
        });
    }

    componentDidMount() {
        this.populateChartData();
    }

    createChartData(panelMarker: PanelMarker) {
        if (!panelMarker || panelMarker.isBlank()) {
            return undefined;
        }
        return MathUtils.round(panelMarker.altChartValue(), panelMarker.field.precision)
    }

    populateChartData() {
        const { panelMarker } = this.state;
        setTimeout(()=>{
            this.setState({
                currentChartData: this.createChartData(panelMarker),
                previousChartData: this.createChartData(panelMarker.previousPanelMarker)
            });
        }, DELAY);  
    }

    adjustChartData(chartData: number): number {
        let value: number = chartData;
        if (chartData < 0) {
            value = 0;
        } else 
        if (chartData > 266.67) {
            value = 266;
        }
        return value;
    }

    render() {
        const { chartContainerClass } = this.props;
        const { panelMarker } = this.state;
        const pm = panelMarker;
        if (!panelMarker.hasValue()) {
            return null;
        }

        const GREEN = '#78F880';
        const YELLOW = '#FEFA83';
        const ORANGE = '#FFD37E';
        const RED = '#FC6552';
        const GRID_LINE_COLOR = '#CDD7EA';
        const BAR_COLOR = 'rgba(255, 255, 255, 0)';

        // If the value goes "off the chart" make it show at the very beginning or end.
        let currentChartData = this.adjustChartData(this.state.currentChartData);
        let previousChartData = this.adjustChartData(this.state.previousChartData);
        let offset = -12;

        const options: Highcharts.Options = {
            chart: {
                type: 'bar',
                marginTop: 20,
                marginBottom: 20,
            },
            // Overlay CURRENT and PREVIOUS series on the same row so the
            // previous blood drop renders at the same vertical position as
            // the current one.
            plotOptions: {
                bar: { grouping: false, pointPlacement: 0 },
            },
            title: {
                text: ''
            },
            xAxis: [{
                labels: {
                    enabled: false
                },
                tickLength: 0
            }],
            yAxis: {
                title: {
                    text: ''
                },
                labels: {
                    enabled: true,
                    formatter: function () {
                        const self: any = this;
                        const digits = panelMarker.field.precision;
                        if (DEBUG) {
                            return self.value;
                        }
                        switch (self.value) {
                            case 0:
                                return `${MathUtils.round(pm.range.optimalMin, digits)}`;
                            case 166.67:
                                return `${MathUtils.round(pm.range.optimalMax, digits)}`;  
                            case 200:
                                return `${MathUtils.round(pm.range.labMax, digits)}`;   
                            case 233.33:
                                return `${MathUtils.round(pm.range.alarmMax, digits)}`;           
                            default:
                                return '';
                        }
                    } 
                },
                min: 0,
                max: 266.67,
                tickPositions: [0, 166.67, 200, 233.33, 266.67],
                gridLineWidth: 1, // removes grid lines between bands if set to 0
                gridLineColor: GRID_LINE_COLOR,
                plotBands: [{
                    label: {
                        // text: 'Optimal',
                        align: 'center',
                        y: -5
                    },
                    from: 0,
                    to: 166.67,
                    color: GREEN
                },
                {
                    label: {
                        // text: 'Lab',
                        align: 'center',
                        y: -5
                    },
                    color: YELLOW,
                    from: 166.67,
                    to: 200
                },
                {
                    label: {
                        // text: '> Lab',
                        align: 'center',
                        y: -5
                    },
                    from: 200,
                    to: 233.33,
                    color: ORANGE
                },
                {
                    label: {
                        // text: 'Alarm',
                        align: 'center',
                        y: -5
                    },
                    from: 233.33,
                    to: 266.67,
                    color: RED
                }
                ]
            },
            tooltip: {
                followPointer: true,
                followTouchMove: true,
                padding: 5,
                useHTML: true,
                formatter: function () {
                    var currentValue = pm && pm.hasValue() ? pm.displayValue() : 'N/A';
                    var previousValue = pm && pm.previousPanelMarker && pm.previousPanelMarker.hasValue() ? pm.previousPanelMarker.displayValue() : 'N/A';
                    return  `<b> Current: ${currentValue}<br>Previous: ${previousValue}</b>`;              
                }
            },
            series: [{
                name: 'CURRENT',
                type: 'bar',
                color: BAR_COLOR,
                borderWidth: 0,
                pointWidth: 10,
                data: [currentChartData],
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    useHTML: true,
                    verticalAlign: 'middle',
                    x: offset,
                    format: `<img src="${blood_drop_current}" />`
                }
            },
            {
                name: 'PREVIOUS',
                type: 'bar',
                color: BAR_COLOR,
                borderWidth: 0,
                pointWidth: 10,
                data: [previousChartData],
                dataLabels: {
                    enabled: true,
                    allowOverlap: true,
                    useHTML: true,
                    verticalAlign: 'middle',
                    x: offset,
                    format: `<img src="${blood_drop_previous}" />`
                }
            }],
            legend: {
                enabled: false
            },
            credits: {
                enabled: false
            },
            exporting: {
                enabled: false
            }
        };

        return (
            <HighchartsReact 
                options={options}
                highcharts={Highcharts}
                containerProps = {{ className: chartContainerClass }}
            />
        );
    }
}